import { DEFAULT_TEMPLATES, labelDocuments, SHEET_LAYOUTS, SheetLayoutKey } from '@iws/pos-devices'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { stringifyGraph } from '@/clients/http/utils'
import { printSheet } from '@/lib/printSheet'
import { ProductModel as ProductApi } from '@/http/models/Product/Product'
import { ProductVariationModel as VariationApi } from '@/http/models/Product/ProductVariation'

type Kind = 'product_label' | 'shelf_label'

interface Row {
  id: string
  name: string
  code: string
  price: number
  copies: number
}

const SHEET_LABEL: Record<SheetLayoutKey, string> = {
  labels_3x8: 'A4, 3 × 8 labels',
  labels_4x10: 'A4, 4 × 10 labels',
  cards_2x5: 'A4, 2 × 5 cards',
}

/**
 * Product and shelf labels on A4 label sheets (POS.md §18.9), from the admin: pick products, each variation becomes a
 * row with its own copies. The barcode is the variation's first barcode, else its SKU; both scan at the till.
 */
export const LabelSheetDialog = ({ open, onOpenChange, shopName }: { open: boolean; onOpenChange: (open: boolean) => void; shopName: string }) => {
  const [search, setSearch] = useState('')
  const [results, setResults] = useState<{ _id: string; name: string }[]>([])
  const [rows, setRows] = useState<Row[]>([])
  const [kind, setKind] = useState<Kind>('product_label')
  const [sheet, setSheet] = useState<SheetLayoutKey>('labels_3x8')

  useEffect(() => {
    if (open) setRows([])
  }, [open])

  useEffect(() => {
    if (!search.trim()) return setResults([])
    let live = true
    const timer = setTimeout(async () => {
      const res = await ProductApi.getList({ query: { search, length: 8, graph_enc: stringifyGraph({ _id: true, name: true }) } })
      if (live) setResults(res.success && res.data ? (res.data.result as { _id: string; name: string }[]) : [])
    }, 250)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [search])

  const addProduct = async (product: { _id: string; name: string }) => {
    setSearch('')
    const res = await VariationApi.getList({ params: { productId: product._id } })
    if (!res.success || !res.data) return toast.error('Could not load the variations')
    const variations = res.data.result
    setRows((list) => [
      ...list,
      ...variations
        .filter((v) => !list.some((r) => r.id === v._id))
        .map((v) => ({
          id: v._id,
          name: variations.length > 1 && v.name ? `${product.name} – ${v.name}` : product.name,
          code: v.barcodes?.[0] || v.sku,
          price: Number(v.regular_price) || 0,
          copies: 1,
        })),
    ])
  }

  const total = rows.reduce((n, r) => n + r.copies, 0)

  const print = () => {
    const items = rows.map((r) => ({
      data: { shop: { name: shopName }, options: { show_price: true }, product: { name: r.name, price: r.price.toFixed(2), unit: 'pc', barcode: r.code } },
      copies: r.copies,
    }))
    const [doc] = labelDocuments(DEFAULT_TEMPLATES[kind], items, sheet)
    if (doc) printSheet(doc, 'Labels')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Print labels</DialogTitle>
          <DialogDescription>Labels with the price and a barcode that scans at the POS, on A4 label sheets.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as Kind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product_label">Product label</SelectItem>
                  <SelectItem value="shelf_label">Shelf label</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Sheet</Label>
              <Select value={sheet} onValueChange={(v) => setSheet(v as SheetLayoutKey)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(SHEET_LAYOUTS) as SheetLayoutKey[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      {SHEET_LABEL[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="relative">
            <Input placeholder="Search products" value={search} onChange={(e) => setSearch(e.target.value)} />
            {results.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-border bg-card shadow-card">
                {results.map((p) => (
                  <li key={p._id}>
                    <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-fill" onClick={() => addProduct(p)}>
                      {p.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="max-h-72 divide-y divide-border-subtle overflow-y-auto rounded-md border border-border">
            {rows.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">Add products to print.</p>}
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate text-ink">{r.name}</span>
                <span className="text-xs text-muted-foreground">{r.code}</span>
                <Input
                  type="number"
                  min={0}
                  aria-label={`Copies of ${r.name}`}
                  className="w-20 text-right tabular-nums"
                  value={r.copies}
                  onChange={(e) => {
                    const copies = Math.max(0, Math.floor(Number(e.target.value) || 0))
                    setRows((list) => list.map((x) => (x.id === r.id ? { ...x, copies } : x)))
                  }}
                />
                <Button variant="ghost" size="icon" aria-label={`Remove ${r.name}`} onClick={() => setRows((list) => list.filter((x) => x.id !== r.id))}>
                  <Trash2 />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={print} disabled={total === 0}>
            Print {total > 0 ? `${total} label${total === 1 ? '' : 's'}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
